import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { reportBreakdown, updateBreakdown, getOpenBreakdown } from '../../truckmateStore';
import { IcWrench } from './cardIcons';

// Report breakdown on a load's card, or a Breakdown update when one is already open: what broke, what the driver says, and pictures dragged in, pasted or chosen. Jarvis makes the breakdown
// email from it. It doesn't call road service or contact customers: the Breakdown button next to it does the customer notice.
const isPhoto = (f) => /^image\//i.test(f.type || '') || /\.(jpe?g|png|heic|heif|webp)$/i.test(f.name || '');
const CANT = 'That picture can\'t be pulled from the other page. Right-click it, choose Copy image, then click here and paste. Or save it and use Choose photos.';

// A picture dragged from a web page may arrive as a link, not a file. Pure.
export function droppedUrl(dt) {
  const list = (dt.getData && dt.getData('text/uri-list')) || '';
  const url = list.split(/\r?\n/).find((l) => l && !l.startsWith('#'));
  if (url) return url;
  const html = (dt.getData && dt.getData('text/html')) || '';
  const m = html.match(/<img[^>]+src=["']([^"']+)/i);
  return m ? m[1] : null;
}

// A pasted screenshot (Win+Shift+S, Snipping Tool, a crop copied from Outlook) can come as a file, as a clipboard item, or as a picture
// inside copied HTML. Synchronous: what's there now, so the paste can be stopped before the browser does anything with it. Pure.
export function clipboardPictures(cd) {
  if (!cd) return { files: [], urls: [] };
  const files = Array.from(cd.files || []).filter(isPhoto);
  const items = files.length ? [] : Array.from(cd.items || []).filter((i) => i && i.kind === 'file' && /^image\//i.test(i.type || '')).map((i) => i.getAsFile()).filter(Boolean);
  const html = (!files.length && !items.length && cd.getData && cd.getData('text/html')) || '';
  const urls = [...String(html).matchAll(/<img[^>]+src=["']([^"']+)["']/gi)].map((m) => m[1]).filter((u) => /^(data:image\/|https?:)/i.test(u));
  return { files: [...files, ...items], urls };
}
let shot = 0;
// a screenshot from the clipboard is always called image.png: give each its own name
const named = (f) => (f.name && !/^image\.(png|jpe?g)$/i.test(f.name) ? f : new File([f], `Screenshot ${shot += 1}.${/jpe?g/i.test(f.type || '') ? 'jpg' : 'png'}`, { type: f.type || 'image/png' }));

function Thumb({ file, onRemove }) {
  const [src, setSrc] = useState('');
  useEffect(() => { if (!URL.createObjectURL) return undefined; const u = URL.createObjectURL(file); setSrc(u); return () => URL.revokeObjectURL(u); }, [file]);
  return (
    <div className="tmw-rb-thumb">
      {src && <img src={src} alt={file.name || 'Breakdown photo'} />}
      <button type="button" aria-label={`Remove ${file.name || 'photo'}`} onClick={onRemove}>×</button>
    </div>
  );
}

function Form({ trip, truck, trailer, open = null, onClose }) {
  // the board hands these over as numbers sometimes: always text, so the checks on send can't crash
  const [ids, setIds] = useState({ truck: String(truck || ''), trailer: String(trailer || ''), trip: String(trip || '') });
  const [part, setPart] = useState('');
  const [problem, setProblem] = useState('');
  const [driverSays, setDriverSays] = useState('');
  const [files, setFiles] = useState([]);
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');
  const [done, setDone] = useState(null);
  const pick = useRef(null);
  const add = (list) => {
    const ok = list.filter(isPhoto);
    if (!ok.length) return false;
    setNote(''); setFiles((cur) => [...cur, ...ok]);
    return true;
  };
  const onDrop = async (e) => {
    e.preventDefault(); setOver(false);
    if (add(Array.from(e.dataTransfer.files || []))) return;
    const url = droppedUrl(e.dataTransfer);
    if (!url) return;
    try {
      const r = await fetch(url);
      if (!r.ok) throw new Error('no');
      const blob = await r.blob();
      if (!add([new File([blob], 'dragged-photo.jpg', { type: blob.type || 'image/jpeg' })])) throw new Error('no');
    } catch { setNote(CANT); }
  };
  // paste anywhere in the form, the text boxes too: pictures attach, and any words still go in the box
  const onPaste = async (e) => {
    const cd = e.clipboardData;
    const { files: pics, urls } = clipboardPictures(cd);
    if (!pics.length && !urls.length) return;
    const words = cd && cd.getData ? String(cd.getData('text/plain') || '').trim() : '';
    if (!words) e.preventDefault();
    if (pics.length) { add(pics.map(named)); return; }
    const got = [];
    for (const u of urls) { try { const r = await fetch(u); if (!r.ok) throw new Error('no'); const b = await r.blob(); if (/^image\//i.test(b.type || '')) got.push(named(new File([b], 'image.png', { type: b.type }))); } catch { /* next one */ } } // eslint-disable-line no-await-in-loop
    if (!got.length || !add(got)) setNote(CANT);
  };
  const send = async (e) => {
    e.preventDefault(); setErr('');
    if (problem.trim().length < 3) { setErr(open ? 'Write the update.' : 'Write what the breakdown is.'); return; }
    if (!open && !ids.trip.trim()) { setErr('Add the trip number.'); return; }
    setBusy(true);
    try { setDone(await (open ? updateBreakdown(trip, { message: problem.trim(), files }) : reportBreakdown(ids.trip.trim(), { part, problem: problem.trim(), driverSays: driverSays.trim(), truck: ids.truck.trim(), trailer: ids.trailer.trim(), files }))); } catch (x) { setErr(x.message || 'It did not go out. Try again.'); } finally { setBusy(false); }
  };
  if (done) {
    return (
      <div className="tmw-bd-body" role="status">
        <b>{open ? 'Update email sent.' : 'Breakdown email sent.'}</b>
        {done.subject && <div className="tmw-small">{done.subject}</div>}
        {done.photosFailed && <div className="tmw-conflict">The photos could not be saved, so they are not in the email. Reply on the email with them.</div>}
        <div className="tmw-source">Road service has not been called and no customers were told.</div>
        <div className="tmw-actions"><button type="button" className="tmw-btn tmw-btn-primary" onClick={onClose}>Close</button></div>
      </div>
    );
  }
  return (
    <form className="tmw-bd-body tmw-rb" onSubmit={send} onPaste={onPaste} noValidate>
      {err && <div className="tmw-conflict" role="alert">{err}</div>}
      {open
        ? <div className="tmw-small">Truck {truck || 'not on file'} · Trailer {trailer || 'not on file'} · Trip {trip}</div>
        : <div className="tmw-rb-ids">{[['truck', 'Truck'], ['trailer', 'Trailer'], ['trip', 'Trip']].map(([k, l]) => <label key={k}>{l}<input value={ids[k]} maxLength={20} onChange={(e) => setIds((c) => ({ ...c, [k]: e.target.value }))} /></label>)}</div>}
      {open ? (
        <>
          <div className="tmw-rb-open"><b>Open breakdown</b><div>{open.reason}</div><div className="tmw-small">Reported by {open.by}{open.updates.length ? ` · ${open.updates.length} update${open.updates.length === 1 ? '' : 's'} so far` : ''}</div></div>
          <label className="tmw-email-compose">What's the update?<textarea rows={4} value={problem} onChange={(e) => setProblem(e.target.value)} placeholder="For example: the tech arrived and is waiting on a part" autoFocus /></label>
        </>
      ) : (
        <>
          <fieldset className="tmw-rb-part">
            <legend>What broke?</legend>
            {[['truck', 'Truck'], ['trailer', 'Trailer'], ['', 'Not sure']].map(([v, l]) => <label key={l}><input type="radio" name="rb-part" checked={part === v} onChange={() => setPart(v)} /> {l}</label>)}
          </fieldset>
          <label className="tmw-email-compose">What's the breakdown?<textarea rows={3} value={problem} onChange={(e) => setProblem(e.target.value)} placeholder="For example: trailer driver's side light came off" autoFocus /></label>
          <label className="tmw-email-compose">What is the driver saying? (optional)<textarea rows={3} value={driverSays} onChange={(e) => setDriverSays(e.target.value)} /></label>
        </>
      )}
      <div className="tmw-label">Photos</div>
      <div className={`tmw-rb-drop${over ? ' is-over' : ''}`} data-testid="rb-drop" tabIndex={0} role="group" aria-label="Breakdown photos"
        onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={onDrop} onFocus={() => setOver(true)} onBlur={() => setOver(false)}>
        <div><b>Paste a screenshot:</b> click here and press Ctrl+V (or paste right in the text box). You can also drag pictures here, or</div>
        <button type="button" className="tmw-btn tmw-btn-sm" onClick={() => pick.current && pick.current.click()}>Choose photos</button>
        <input ref={pick} type="file" accept="image/*,.heic,.heif" multiple hidden aria-label="Choose photos" onChange={(e) => { add(Array.from(e.target.files || [])); e.target.value = ''; }} />
      </div>
      {note && <div className="tmw-small" role="status">{note}</div>}
      {files.length > 0 && <div className="tmw-rb-thumbs">{files.map((f, i) => <Thumb key={`${f.name}${i}`} file={f} onRemove={() => setFiles((cur) => cur.filter((_, n) => n !== i))} />)}</div>}
      {err && <div className="tmw-conflict" role="status">{err}</div>}
      <div className="tmw-actions">
        <button type="submit" className="tmw-btn tmw-btn-danger" disabled={busy}>{busy ? 'Sending…' : open ? 'Send update email' : 'Send breakdown email'}</button>
        <button type="button" className="tmw-btn" onClick={onClose}>Cancel</button>
      </div>
      <div className="tmw-source">This emails the breakdown team{open ? ' on the same chain' : ''}. It doesn't call road service or contact customers.</div>
    </form>
  );
}

export default function ReportBreakdown({ trip, truck, trailer, hasOpen = false }) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState(undefined);   // undefined while checking, null = none open, or the open breakdown
  const close = () => setOpen(false);
  const show = async (e) => {
    e.stopPropagation(); setState(undefined); setOpen(true);
    try { setState(((await getOpenBreakdown(trip)) || {}).open || null); } catch { setState(null); }   // can't tell: the server still refuses a second report
  };
  return (
    <>
      <button type="button" className="tmw-btn tmw-btn-sm" onClick={show}><IcWrench /> {hasOpen ? 'Breakdown update' : 'Report breakdown'}</button>
      {open && createPortal(
        <div className="truckmate-workspace tmw-viewer-root"><div className="tmw-viewer" role="dialog" aria-modal="true" aria-label={`Report breakdown for load ${trip}`} onDragOver={(e) => e.preventDefault()} onDrop={(e) => e.preventDefault()}>
          <div className="tmw-viewer-box tmw-bd-box" onClick={(e) => e.stopPropagation()}>
            <div className="tmw-viewer-head"><b>{state ? 'Breakdown update' : 'Report breakdown'}</b><button type="button" className="tmw-btn tmw-btn-sm" onClick={close}>Close</button></div>
            {state === undefined ? <div className="tmw-bd-body">Checking this load…</div> : <Form trip={trip} truck={truck} trailer={trailer} open={state} onClose={close} />}
          </div>
        </div></div>, document.body)}
    </>
  );
}
